/**
 * © [2023] Conduent, Inc. All rights reserved. Conduent®and Conduent Agile
 * Star®are trademarks of Conduent, Inc. and/or its subsidiaries in the United
 * States and/or other countries.
 */
import moment from 'moment';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { checkMark, uncheckedMark } from './base64Images';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const getHeader = (data) => {
  const { contractSeqNum, contractName, prinDate, requestedBy, reportName } =
    data;
  const header = {
    alignment: 'center',
    fontSize: 10,
    margin: [28.8, 20, 28.8, 25.92],
    style: 'tableExample',
    table: {
      widths: [175, 190, 148],
      body: [
        [
          {
            text: [{ text: 'Requested By: ', bold: true }, requestedBy],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
            border: [false, false, false, false],
          },
          {
            text: reportName,
            fontSize: 12,
            bold: true,
            fontFamily: 'arial',
            alignment: 'center',
            border: [false, false, false, false],
          },
          {
            text: [{ text: `Date Printed:`, bold: true }, prinDate],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
            border: [false, false, false, false],
          },
        ],
        [
          {
            text: [{ text: 'Contract ID:', bold: true }, contractSeqNum],
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'left',
            border: [false, false, false, true],
          },
          {
            text: contractName,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'center',
            decorationStyle: 'dotted',
            border: [false, false, false, true],
          },
          {
            text: `Page 1 of 1`,
            fontSize: 10,
            fontFamily: 'arial',
            alignment: 'right',
            border: [false, false, false, true],
          },
        ],
      ],
    },
    // layout: 'noBorders',
  };
  return header;
};

const tableLayout = {
  hLineWidth: function (i, node) {
    return i === 0 || i === node.table.body.length ? 1 : 1;
  },
  vLineWidth: function (i, node) {
    return i === 0 || i === node.table.widths.length ? 1 : 1;
  },
  hLineColor: function (i, node) {
    return i === 0
      ? '#e6e6e6'
      : i === node.table.body.length
      ? 'black'
      : 'white';
  },
  vLineColor: function (i, node) {
    return i === 0 || i === node.table.widths.length ? 'black' : 'blue';
  },
  paddingLeft: function (i, node) {
    return 20;
  },
  paddingRight: function (i, node) {
    return 40;
  },
  paddingTop: function (i, node) {
    return 20;
  },
  paddingBottom: function (i, node) {
    return 5;
  },
};
const headerTableMargin = [-15, 10, 0, 0];
const innerTableWidths = [120, 120, 105, 95];
const pageMargins = [42, 80, 28.8, 25.92];
const getHeaderObject = (text) => {
  return {
    margin: [0, -27, 0, 20],
    text: ` ${text}          `,
    background: 'white',
    fontWeight: 'bold',
    bold: true,
    fontSize: 10,
  };
};
const safe = (value) => (value === null || value === undefined ? '' : value);

const getColumn = (text, colNum) => {
  text=safe(text);
  if (colNum === 0 || colNum === 2) {
    if (text == 'Desired Outputs') {
      return {
        text: '' + text,
        fontSize: 10,
        alignment: 'left',
        margin: [-55, 0, 0, 0],
        bold: true,
      };
    } else if (text.includes('Pricing Timing')) {
      return {
        text: ' ' + text,
        fontSize: 10,
        alignment: 'left',
        margin: [-90, 0],
        bold: true,
        colSpan: 2,
      };
    } else {
      return {
        text: text,
        bold: true,
        fontSize: 10,
        alignment: 'right',
      };
    }
  }

  if (colNum === 1 || colNum === 3) {
    if (text === 'CMS Invoice') {
  return {
    text: text,
    fontSize: 10,
    alignment: 'left',
    margin: [-110, 0, 0, 0], // Shift left within the center cell
    bold: true,
  };
}

    if (text === 'checked') {
      return {
        width: 12,
        marginTop: -3,
        image: checkMark,
      };
    }
    if (text === 'unchecked') {
      return {
        width: 12,
        marginTop: -1,
        image: uncheckedMark,
      };
    }
    if (text == 'Final Quarter') {
      return {
        text: ' ' + text,
        fontSize: 10,
        alignment: 'left',
        margin: [15, 0],
        bold: true,
      };
    }

    return { text: ' ' + text, fontSize: 10, alignment: 'left'}; 
  }
};
const getTableRow = (colTexts) => {
  return [
    getColumn(colTexts[0], 0),
    getColumn(colTexts[1], 1),
    getColumn(colTexts[2], 2),
    getColumn(colTexts[3], 3),
  ];
};

const getTableBody = (rows) => {
  const tableRows = [];
  for (let i = 0; i < rows.length; i++) {
    tableRows.push(getTableRow(rows[i]));
  }
  return tableRows;
};

const getOneContainer = (heading, tableRows) => {
  return {
    style: 'tableExample',
    color: 'black',
    margin: headerTableMargin,
    table: {
      body: [
        [getHeaderObject(heading)],
        [
          {
            margin: [0, -45, 25, 0],
            table: {
              heights: 12,
              widths: innerTableWidths,
              body: getTableBody(tableRows),
            },
            layout: 'noBorders',
          },
        ],
      ],
    },
    layout: tableLayout,
  };
};

const getPDFData = (data, headerData) => {
  let content = [];
  for (let key in data) {
    content.push(getOneContainer(key, data[key]));
  }

  const currentYear = new Date().getFullYear();
  content.push({
    text: `@${currentYear} Conduent Business Services, LLC. All rights reserved. Conduent and Conduent Agile Star are trademarks of Conduent Business Services, LLC in the United States and/or other countries.`,
    alignment: 'left',
    fontSize: 9,
    margin: [0, 30, 0, 0], 
  });

  const pdfData = {
    header: getHeader(headerData),
    pageMargins: pageMargins,
    content: content,
    styles: {},
    defaultStyle: {
      alignment: 'justify',
    },
  };
  return pdfData;
};



export const generateContractViewReport = (data, UserDefaultValues, userId) => {
  const {
    rebaterebateProgramCd,
    contractSeqNum,
    contractName,
    startDt,
    endDt,
    writeOffDt,
    lastUpdateDt,
    lastUpdateUserId,
    contractValidInd,
    useDefMfgInd,
    useDefRpcInd,
    mfgId,
    mfgName,
    defEnrollmentTimingCd,
    defPricingTimingCd,
    defPaperInd,
    defPaperFinalQtr,
    defCmsElectInd,
    defCmsElectFinalQtr,
    defPrintImageInd,
    defPrintImageFinalQtr,
    defNcpdpSummaryInd,
    defNcpdpSummaryFinalQtr,
    defNcpdpClaimOwnInd,
    defNcpdpClaimOwnFinalQtr,
    defNcpdpClaimMbInd,
    defNcpdpClaimMbFinalQtr,
    defEarliestQtrForDrugDtls,
    defDetailLevelOnInvoiceCd,
    rebateProgramCd,
    progDefDispSpecialist,
    contactTypeCd,
    contactName,
    corpName,
    contactSeqNum,
    addrLine1,
    addrLine2,
    addrLine3,
    phoneNum,
    phoneExtension,
    addr_line_4,
    faxNum,
    email,
    key,
    keyTypeCd,
    invContactSeqNum,
    dispContactSeqNum,
  } = data;

  const formatPhoneNumber = (number) => {
    if (!number) return '';
    const cleaned = number.toString().replace(/\D/g, '');
    if (cleaned.length !== 10) return number;
    return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`;
  };
  const invoiceContact = data.contacts?.find(c => c.contactMode === "INVOICE") || {};
  const disputeContact = data.contacts?.find(c => c.contactMode === "DISPUTE") || {};

  
  const dataForPDF = {
    'General Information': [
      ['Start Date:', safe(startDt), 'End Date:', safe(endDt)],
      [
        'Write off Date:',
        safe(writeOffDt),
        'Contract Valid:',
        contractValidInd === 1 ? 'checked' : 'unchecked',
      ],
      [
        'Last Updated Date:',
        safe(lastUpdateDt),
        'Last Updated ID:',
        safe(lastUpdateUserId),
      ],
    ],
  
    'Manufacturer Information': [
      ['Manufacturer ID:', safe(mfgId), 'Manufacturer Name:', safe(mfgName)],
      [
        'Use Mfg Defaults:',
        useDefMfgInd === 1 ? 'checked' : 'unchecked',
        `Pricing Timing: ${safe(defPricingTimingCd)}                     Enrollment Timing: ${safe(defEnrollmentTimingCd)}`,
        '',
      ],
      ['', '', 'Desired Outputs', ''],
      ['', 'Final Quarter', '', 'Final Quarter'],
      [
        'Paper:',
        defPaperInd === 1 ? 'checked' : 'unchecked',
        'NCPDP:',
        defNcpdpSummaryInd === 1 ? 'checked' : 'unchecked',
      ],
      [
        'CMS Formate File:',
        'checked',
        'NCPDP Own Claims:',
        defNcpdpClaimOwnInd === 1 ? 'checked' : 'unchecked',
      ],
      [
        'Print Image:',
        defPrintImageInd === 1 ? 'checked' : 'unchecked',
        'NCPDP MB Claims:',
        'unchecked',
      ],
    ],
  
    'Rebate Program Information': [
      [
        'Rebate Program:',
        safe(rebateProgramCd),
        'Earliest Qtr for Details:',
        safe(defEarliestQtrForDrugDtls),
      ],
      [
        'Use RPC Defaults:',
        useDefRpcInd === 1 ? 'checked' : 'unchecked',
        'Detail Level on Invoice:',
        defDetailLevelOnInvoiceCd === 'NO'
          ? 'NDC Totals Only'
          : defDetailLevelOnInvoiceCd === 'QO'
          ? 'Quarter Totals Only'
          : safe(defDetailLevelOnInvoiceCd) || 'None',
      ],
      ['Default Dispute Specialist:', safe(progDefDispSpecialist), '', ''],
    ],
  
    'Invoice Contact': [
      ['Contact Type:', `${safe(invoiceContact.keyTypeCd)}  ${safe(invoiceContact.key)}`, '', 'CMS Invoice'],
      ['Contact Name:', safe(invoiceContact.contactName), '', ''],
      ['Corporation Name:', safe(invoiceContact.corpName), '', ''],
      ['Contact Address:', safe(invoiceContact.addrLine1), 'Phone Nbr:', safe(formatPhoneNumber(invoiceContact.phoneNum))],
      ['', safe(invoiceContact.addrLine2), 'Ext:', safe(invoiceContact.phoneExtension)],
      ['', safe(invoiceContact.addrLine3), 'Fax Nbr:', safe(invoiceContact.faxNum)],
      ['', safe(invoiceContact.addrLine4), 'Email:', safe(invoiceContact.emailAddr)],
      ['', '', '', ''],
    ],

  
    'Dispute Contact': [
      ['Contact Type:', `${safe(disputeContact.keyTypeCd)}  ${safe(disputeContact.key)}`, '', 'CMS Invoice'],
      ['Contact Name:', safe(disputeContact.contactName), '', ''],
      ['Corporation Name:', safe(disputeContact.corpName), '', ''],
      ['Contact Address:', safe(disputeContact.addrLine1), 'Phone Nbr:', safe(formatPhoneNumber(disputeContact.phoneNum))],
      ['', safe(disputeContact.addrLine2), 'Ext:', safe(disputeContact.phoneExtension)],
      ['', safe(disputeContact.addrLine3), 'Fax Nbr:', safe(disputeContact.faxNum)],
      ['', safe(disputeContact.addrLine4), 'Email:', safe(disputeContact.emailAddr)],
      ['', '', '', ''],
    ],

  };
  

  const headerData = {
    contractSeqNum,
    contractName,
    prinDate: moment(new Date()).format('MM/DD/YYYY'),
    requestedBy:
      UserDefaultValues && UserDefaultValues.userId
        ? UserDefaultValues.userId
        : userId,
    reportName: 'Contract Report',
  };
  const pdfData = getPDFData(dataForPDF, headerData);
  pdfMake.createPdf(pdfData).download(`Contract Report.pdf`);
};

const getContractValidationContent = (data) => {
  const { msgId, msgText } = data;
  const content = [
    {
      style: 'tableExample',
      margin: [0, 0, 0, 50],
      table: {
        headerRows: 1,
        widths: ['*'],
        body: [
          [{ text: '' }],
          [
            {
              text: 'General Contract Errors',
              style: 'tableHeader',
              bold: true,
              marginTop: 10,
            },
          ],
        ],
      },
      layout: 'headerLineOnly',
    },
    {
      style: 'tableExample',
      table: {
        headerRows: 1,
        widths: [100, '*'],
        body: [
          [
            { text: 'Message ID', style: 'tableHeader', bold: true },
            { text: 'Message Error', style: 'tableHeader', bold: true },
          ],
          [msgId, msgText],
        ],
      },
      layout: 'headerLineOnly',
    },
  ];

  return content;
};

const getContractValidationPDFData = (data, headerData) => {
  return {
    header: getHeader(headerData),
    content: getContractValidationContent(data),
    pageMargins,
  };
};
export const generateContractValidationReport = (
  data,
  UserDefaultValues,
  logInUser
) => {
  const { contractName, contractSeqNum } = data;
  const headerData = {
    contractSeqNum,
    contractName,
    prinDate: moment(new Date()).format('MM/DD/YYYY'),
    requestedBy:
      UserDefaultValues && UserDefaultValues.userId
        ? UserDefaultValues.userId
        : logInUser,
    reportName: 'Contract Validation Report',
  };
  const pdfData = getContractValidationPDFData(data, headerData);
  pdfMake.createPdf(pdfData).download(`ContractValidationReport.pdf`);
};
